import { fabric } from 'fabric-with-gestures';
import { severityFor, DEFAULTS } from './ratio';

// Re-exported so the one import of the contrast maths elsewhere keeps working.
export { relativeLuminance, contrastRatio, colorDifference, severityFor, DEFAULTS } from './ratio';

// fabric.Color reads hex, rgb(), rgba() and named colours. A gradient or pattern
// fill has no single colour to judge, so it returns null and the text is skipped
// rather than guessed at.
function readColor(fill) {
	if (typeof fill !== 'string' || !fill) return null;
	try {
		const source = new fabric.Color(fill).getSource();
		return source ? [source[0], source[1], source[2]] : null;
	} catch (e) {
		return null;
	}
}

function toHex([r, g, b]) {
	return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}


export default class ContrastTools {
	constructor({ canvasRef, thresholds } = {}) {
		this.canvasRef = canvasRef;
		this.thresholds = { ...DEFAULTS, ...(thresholds || {}) };
		this._shirt = null;
		this._mask = null;
		this._unreadable = false;
	}

	// The shirt drawn on its own. The text has to be measured against what is
	// behind it, not against the composite it is already part of - reading the live
	// canvas would sample the glyphs themselves. Cached per background image, so it
	// is rebuilt only when the side or the shirt colour changes.
	_shirtContext(canvas) {
		const bg = canvas.backgroundImage;
		const element = bg && bg.getElement && bg.getElement();
		if (!element || !element.width) return null;

		const key = `${element.src}|${canvas.width}x${canvas.height}`;
		if (this._shirt && this._shirt.key === key) return this._shirt.ctx;

		const el = document.createElement('canvas');
		el.width = canvas.width;
		el.height = canvas.height;
		const ctx = el.getContext('2d', { willReadFrequently: true });
		// Mirror how loadSide places the background: scaled to fill the canvas.
		ctx.drawImage(
			element,
			bg.left || 0, bg.top || 0,
			element.width * (bg.scaleX || 1), element.height * (bg.scaleY || 1)
		);
		this._shirt = { key, ctx };
		return ctx;
	}

	// One reusable full-size canvas for the glyph masks. Full size on purpose: an
	// object rendered into it lands on the same coordinates as on the real canvas,
	// so a mask pixel maps straight onto the shirt pixel behind it with no offset
	// arithmetic. Valid because this editor never zooms or pans the viewport.
	_maskContext(canvas) {
		if (!this._mask || this._mask.canvas.width !== canvas.width || this._mask.canvas.height !== canvas.height) {
			const el = document.createElement('canvas');
			el.width = canvas.width;
			el.height = canvas.height;
			this._mask = { canvas: el, ctx: el.getContext('2d', { willReadFrequently: true }) };
		}
		return this._mask.ctx;
	}

	// Returns one entry per text that is hard to read, empty when everything is
	// fine. Callers post the whole list so the frontend can also clear a warning.
	check() {
		const canvas = this.canvasRef && this.canvasRef.current;
		if (!canvas || this._unreadable) return [];

		const shirtCtx = this._shirtContext(canvas);
		if (!shirtCtx) return [];

		const warnings = [];
		canvas.getObjects().forEach((obj, index) => {
			if (obj.type !== 'text') return;
			const result = this._evaluate(canvas, shirtCtx, obj);
			if (result && result.severity == 'invisible') warnings.push({ index, text: obj.text, ...result });
		});
		return warnings;
	}

	_evaluate(canvas, shirtCtx, obj) {
		const textColor = readColor(obj.fill);
		if (!textColor) return null;

		// Clip to the canvas: text dragged half off the shirt still gets judged on
		// the part that is actually on it.
		const rect = obj.getBoundingRect(true, true);
		const left = Math.max(0, Math.floor(rect.left));
		const top = Math.max(0, Math.floor(rect.top));
		const width = Math.min(canvas.width, Math.ceil(rect.left + rect.width)) - left;
		const height = Math.min(canvas.height, Math.ceil(rect.top + rect.height)) - top;
		if (width <= 0 || height <= 0) return null;

		const maskCtx = this._maskContext(canvas);
		maskCtx.clearRect(0, 0, canvas.width, canvas.height);
		obj.render(maskCtx);

		let mask, shirt;
		try {
			mask = maskCtx.getImageData(left, top, width, height).data;
			shirt = shirtCtx.getImageData(left, top, width, height).data;
		} catch (e) {
			// A shirt image served without CORS headers taints the canvas and makes
			// the pixels unreadable. Go quiet instead of warning on a guess.
			console.warn('Contrast check unavailable, canvas pixels cannot be read:', e);
			this._unreadable = true;
			return null;
		}

		// Bucket the shirt colours sitting behind actual glyph pixels. Quantising to
		// 5 bits per channel stops jpeg noise and soft shading from splitting one
		// panel into hundreds of near-identical entries, while genuinely different
		// panels on a multi-coloured shirt still land in separate buckets.
		const buckets = new Map();
		const step = this.thresholds.sampleStep;
		let glyphPixels = 0;
		for (let y = 0; y < height; y += step) {
			for (let x = 0; x < width; x += step) {
				const i = (y * width + x) * 4;
				if (mask[i + 3] < 128) continue;
				glyphPixels++;
				const r = shirt[i], g = shirt[i + 1], b = shirt[i + 2];
				const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
				const bucket = buckets.get(key);
				if (bucket) {
					bucket.count++;
				} else {
					buckets.set(key, { count: 1, color: [r, g, b] });
				}
			}
		}
		if (glyphPixels === 0) return null;

		// Severity is decided per band, not by the single worst pixel: a dark seam
		// crossing two letters should not report the whole text as invisible while
		// the bulk of it sits at a merely weak contrast.
		let invisibleCount = 0, lowCount = 0;
		let worstInvisible = null, worstLow = null;
		buckets.forEach(bucket => {
			const { severity, ratio } = severityFor(textColor, bucket.color, this.thresholds);
			if (severity === 'invisible') {
				invisibleCount += bucket.count;
				if (!worstInvisible || ratio < worstInvisible.ratio) worstInvisible = { ratio, color: bucket.color };
			} else if (severity === 'low') {
				lowCount += bucket.count;
				if (!worstLow || ratio < worstLow.ratio) worstLow = { ratio, color: bucket.color };
			}
		});

		const invisibleCoverage = invisibleCount / glyphPixels;
		const lowCoverage = (invisibleCount + lowCount) / glyphPixels;

		let severity = null, worst = null, coverage = 0;
		if (invisibleCoverage >= this.thresholds.minCoverage) {
			severity = 'invisible';
			worst = worstInvisible;
			coverage = invisibleCoverage;
		} else if (lowCoverage >= this.thresholds.minCoverage) {
			severity = 'low';
			worst = worstLow || worstInvisible;
			coverage = lowCoverage;
		}
		if (!severity || !worst) return null;

		return {
			severity,
			ratio: Math.round(worst.ratio * 100) / 100,
			coverage: Math.round(coverage * 100) / 100,
			textColor: toHex(textColor),
			shirtColor: toHex(worst.color)
		};
	}

	// The shirt cache keys on the background image, so a new side or colour rebuilds
	// it by itself. This is only needed if a shirt image is swapped in place.
	reset() {
		this._shirt = null;
	}
}
